// components/AppHeader.tsx
import { lazy, Suspense, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { isAnalyzerPage as checkIsAnalyzerPage } from '@/message-analyzer/lib/analyzer-utils'
import { MoreHorizontal, Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppMenu } from '@/components/shared/navigation/AppMenu'
import { ContextSwitcher } from '@/components/routing/ContextSwitcher'
import { LinaLensFullLogo, UniquelyNDFullLogo, AskLinaRowLogo } from '@lina/brand-logos'
import { useAuth } from '@/contexts/AuthContext'

const AppHelpModal = lazy(() =>
  import('@/components/shared/navigation/AppHelpModal').then((module) => ({
    default: module.AppHelpModal,
  })),
)

const LensHelpModal = lazy(() =>
  import('@/components/shared/navigation/LensHelpModal').then((module) => ({
    default: module.LensHelpModal,
  })),
)

// Debug logging toggle - set to false to reduce console noise
const DEBUG = false
const log = DEBUG ? console.log : () => {}

interface AppHeaderProps {
  currentStep?: number | string
  isAdmin?: boolean
  onStartOver?: () => void
  onSubmitReport?: (issueType: string, details: string) => Promise<void>
  showMenuButton?: boolean
}

export const AppHeader = ({
  currentStep,
  isAdmin = false,
  onStartOver,
  showMenuButton = true,
}: AppHeaderProps) => {
  const [showMenu, setShowMenu] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const location = useLocation()
  const { accountData } = useAuth()
  const isLensPage = location.pathname === '/lens' || location.pathname.startsWith('/lens/')
  const isAdminPage = location.pathname.startsWith('/admin')

  // Debug logging
  useEffect(() => {
    log('[AppHeader] currentStep:', currentStep)
    log('[AppHeader] location.pathname:', location.pathname)
    log('[AppHeader] isAnalyzerPage:', location.pathname === '/')
  }, [currentStep, location.pathname, accountData])

  // ALWAYS show help icon on analyzer page - regardless of step
  const isAnalyzerPage = checkIsAnalyzerPage(location.pathname)
  const showHelpIcon = isAnalyzerPage || isLensPage

  // Determine which step/tab to show in help modal
  const helpStep = currentStep ?? 1
  const lensHelpTab: 'input' | 'results' =
    location.pathname === '/lens/results' ? 'results' : 'input'

  return (
    <>
      <nav
        id="navigation"
        tabIndex={-1}
        className="fixed top-0 left-0 right-0 z-40 glass bg-background/70 backdrop-blur-md border-b border-gray-100 dark:border-gray-700 shadow-none"
      >
        <div className="max-w-full mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo + greeting - left */}
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-2 cursor-pointer">
                {isLensPage ? (
                  <>
                    <LinaLensFullLogo size={25} className="shrink-0" />
                    <p className="text-fluid-xs font-medium text-foreground tracking-tight min-w-0 truncate">
                      Create communication that works for every mind.
                    </p>
                  </>
                ) : isAdminPage ? (
                  <UniquelyNDFullLogo size={25} className="shrink-0" />
                ) : (
                  <AskLinaRowLogo size={25} className="" />
                )}
              </Link>
              {/*
              {user && accountData && (
                <span className="text-fluid-sm font-medium text-muted-foreground hidden sm:inline">
                  Hi {isFreeAccount ? 'Anonymous User' : (accountData.first_name || 'there')}
                </span>
              )}
              */}
            </div>

            {/* Context switcher + actions — right */}
            <div className="flex items-center gap-2">
              <ContextSwitcher />
              {/* Help Icon - Always show on analyzer and household pages */}
              {showHelpIcon && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    console.log('[AppHeader] Help button clicked')
                    setShowHelp(true)
                  }}
                  aria-label="Help"
                  className="text-foreground hover:text-yellow-600 dark:hover:text-yellow-400 btn-yellow-glow"
                >
                  <Lightbulb className="p-0" />
                </Button>
              )}

              {showMenuButton && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowMenu(!showMenu)}
                    aria-label="App Menu"
                    className="text-foreground hover:text-secondary btn-secondary-glow transition-colors"
                  >
                    <MoreHorizontal className="p-0" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <AppMenu isOpen={showMenu} onClose={() => setShowMenu(false)} onStartOver={onStartOver} />
      </nav>

      {/* Contextual Help Modal - lazy loaded only when help is opened */}
      {showHelp && (
        <Suspense fallback={null}>
          {isLensPage ? (
            <LensHelpModal
              open={showHelp}
              onClose={() => {
                console.log('[AppHeader] Closing lens help modal')
                setShowHelp(false)
              }}
              defaultTab={lensHelpTab}
            />
          ) : (
            <AppHelpModal
              open={showHelp}
              onClose={() => {
                console.log('[AppHeader] Closing help modal')
                setShowHelp(false)
              }}
              step={helpStep}
            />
          )}
        </Suspense>
      )}
    </>
  )
}
